import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { publicApi } from "../hooks/useApi";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await publicApi.post("/auth/password/reset", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/sign-in"), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail?.message || "This reset link is invalid or has expired.");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-4" data-testid="reset-password-missing-token">
        <p className="text-white/60 mb-4">This reset link is missing or invalid.</p>
        <Link to="/forgot-password" className="underline text-sm">Request a new link</Link>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="text-center mb-8">
        <p className="font-display text-xl tracking-[0.2em] uppercase text-white">Endure Beyond</p>
        <p className="text-white/50 text-sm mt-2">Choose a new password</p>
      </div>
      {done ? (
        <p className="text-sm text-white/60" data-testid="reset-password-done-message">Password updated. Redirecting to sign in…</p>
      ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-4">
          {error && <p data-testid="reset-password-error" className="text-sm text-red-400">{error}</p>}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"} required minLength={8} placeholder="New password (min 8 characters)" aria-label="New password, minimum 8 characters" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input"
              className="w-full bg-surface border border-border px-4 py-3 pr-11 text-sm"
            />
            <button
              type="button" onClick={() => setShowPassword((v) => !v)} data-testid="reset-password-toggle-visibility"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button
            type="submit" disabled={busy} data-testid="reset-password-submit-button"
            className="w-full bg-foreground text-background px-5 py-3 text-sm uppercase tracking-wide disabled:opacity-50"
          >
            {busy ? "Updating…" : "Update Password"}
          </button>
        </form>
      )}
    </div>
  );
}
